import classnames from 'classnames';
import React, { createContext, useContext, useState, useMemo, ReactNode, Suspense } from 'react';
import { guid } from 'redux-database/dist/util';
import { wait } from '../../util/async';
import { nextAnimationFrame } from '../../util/ui-helpers';
import { Card } from '../Card/Card';
import './Modal.css';
import { Icon } from '../Icon/Icon';
import ErrorBoundary from '../../pages/ErrorBoundary/ErrorBoundary';

export type ModalSize = 'small' | 'large' | 'xs' | 'xl';

interface ModalOptions {
  open: boolean;
  title?: React.ReactNode;
  size?: ModalSize;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  overflow?: boolean;
  position?: 'center' | 'aside';
  scrollShadows?: boolean;
  withSelectedItems?: boolean;
}

function Modal({
  children,
  title,
  size = 'large',
  footer,
  position = 'center',
  open,
  overflow,
  actions,
  scrollShadows = true,
  withSelectedItems,
}: ModalOptions): JSX.Element {
  return (
    <div
      className={classnames('modal', `modal--${size}`, `modal--${position}`, {
        'modal--open': open,
        'modal--overflow': overflow,
      })}
    >
      <div className="modal__background" />
      <Card
        title={title}
        size="large"
        footer={footer}
        elevation={8}
        actions={actions}
        scrollShadows={!overflow && scrollShadows}
        withSelectedItems={withSelectedItems}
      >
        {children}
      </Card>
    </div>
  );
}

interface PresenterOptions {
  size?: ModalSize;
}

interface ModalPresenter {
  /// Close the modal with the given handle
  close(handle?: string): void;
  /// Present a modal with the given component and props
  present<Component extends React.FC<object>>(
    component: Component,
    props: React.ComponentProps<Component>,
    options?: PresenterOptions
  ): Promise<void>;
}

const ModalPresenterContext = createContext<ModalPresenter | null>(null);

interface ModalInfo {
  id: string;
  handle?: string;
  open: boolean;
  size?: ModalSize;
  close: () => void;
  Modal: (options: ModalOptions) => JSX.Element;
}

const ModalInfoContext = createContext<ModalInfo | null>(null);

export function useModalPresenter(): ModalPresenter {
  const presenter = useContext(ModalPresenterContext);
  if (!presenter) {
    throw new Error('ModalPresenterContext not found');
  }
  return presenter;
}

export function useModal(): ModalInfo {
  const modal = useContext(ModalInfoContext);
  if (!modal) {
    throw new Error('ModalPresenterContext not found');
  }
  return modal;
}

interface ModalData<Props extends object> {
  id: string;
  handle?: string;
  props: Props;
  open: boolean;
  component: React.FC<Props>;
  options?: PresenterOptions;
}

function ModalContainer({
  modal,
  options,
  closeId,
}: {
  modal: ModalData<{}>;
  options?: PresenterOptions;
  closeId: (id: string) => void;
}): JSX.Element {
  return (
    <ModalInfoContext.Provider
      value={{
        id: modal.id,
        handle: modal.handle,
        close: () => closeId(modal.id),
        Modal,
        open: modal.open,
        size: options?.size,
      }}
    >
      <ErrorBoundary withModal>
        <Suspense
          fallback={
            <Modal open size={options?.size}>
              <div className="modal__loading">
                <Icon name="edit" loading />
              </div>
            </Modal>
          }
        >
          {<modal.component {...modal.props} />}
        </Suspense>
      </ErrorBoundary>
    </ModalInfoContext.Provider>
  );
}

export function ModalWrapper({ children }: { children: ReactNode }): JSX.Element {
  const [modals, setModals] = useState<ModalData<{}>[]>([]);

  const closeId = useMemo(() => {
    return async (id: string) => {
      setModals((modals) => modals.map((modal) => (modal.id === id ? { ...modal, open: false } : modal)));
      await wait(1);
      setModals((modals) => modals.filter((e) => e.id !== id));
    };
  }, [setModals]);

  const presenter: ModalPresenter = useMemo(() => {
    return {
      close: (handle) => modals.filter((modal) => modal.handle === handle).forEach((modal) => closeId(modal.id)),
      async present(component, props, options) {
        const id = guid();
        const newModal: ModalData<{}> = {
          props,
          component,
          open: false,
          id,
          options,
        };
        setModals((modals) => [...modals, newModal]);
        await nextAnimationFrame();
        setModals((modals) => modals.map((modal) => (modal.id === id ? { ...modal, open: true } : modal)));
      },
    };
  }, [modals]);

  // useEffect(() => {
  //   document.body.classList.toggle('with-modal', modals.length > 0);
  // }, [modals.length]);

  return (
    <div>
      {modals.length > 0 &&
        modals.map((modal) => (
          <ModalContainer key={modal.id} modal={modal} options={modal.options} closeId={closeId} />
        ))}
      <ModalPresenterContext.Provider value={presenter}>{children}</ModalPresenterContext.Provider>
    </div>
  );
}
